import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import * as Sentry from '@sentry/react';
import { getProject } from './store/project';
import {
  getEnvironment,
  isEnvironment,
} from '@shared/helpers/environment.helpers';
import { NodonEnvironment } from '@shared/models/environment.model';
import { getConfig } from './providers/ConfigProvider';
import { getProjectGfa } from '@shared/helpers/project.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';

const SAMPLE_RATE: Record<NodonEnvironment, number> = {
  [NodonEnvironment.Localhost]: 0,
  [NodonEnvironment.Development]: 1,
  [NodonEnvironment.Production]: 0.05,
};

export const initAmplitude = async () => {
  const currentEnvironment = getEnvironment();
  const config = await getConfig();
  const apiKey = config.amplitudeApiKey;

  if (!apiKey) {
    console.error('Amplitude API key is not set');
    return;
  }

  amplitude.init(apiKey, {
    // None is best-effort: Amplitude's debug-logs cookie can still enable Debug.
    // isAmplitudeLoggerNoiseEvent in Sentry beforeSend is the guarantee that
    // those console.error lines do not become issues. Tracking stays on.
    logLevel: amplitude.Types.LogLevel.None,
    defaultTracking: {
      sessions: true,
    },
  });
  const replay = sessionReplayPlugin({
    sampleRate: SAMPLE_RATE[currentEnvironment],
  });
  amplitude.add(replay);
};

// Gather other properties to automatically send to amplitude as event properties
const getMetaProperties = (): Record<string, string | number> => {
  const metaProperties: Record<string, string | number> = {};

  const currentProject = getProject();
  if (currentProject) metaProperties.ProjectId = currentProject.id;

  if (currentProject)
    metaProperties.GFA = Math.round(getProjectGfa(currentProject));

  return metaProperties;
};

/**
 * Masks string values (and strings nested in arrays) so breadcrumb privacy
 * matches Sentry Replay `maskAllText`. Non-string values stay intact.
 */
const maskBreadcrumbValue = (value: unknown): unknown => {
  if (guards.is.string(value)) return '*'.repeat(value.length);
  if (guards.is.array(value)) return value.map(maskBreadcrumbValue);
  return value;
};

/**
 * Applies {@link maskBreadcrumbValue} to each property in a breadcrumb data object.
 */
const maskBreadcrumbData = (
  data: Record<string, unknown>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      maskBreadcrumbValue(value),
    ]),
  );

/**
 * Mirrors an Amplitude event as a Sentry breadcrumb so replays/errors show product analytics context.
 * String property values are masked to align with Replay `maskAllText`.
 */
const addAmplitudeBreadcrumb = (
  eventType: string,
  eventProperties: Record<string, unknown>,
): void => {
  Sentry.addBreadcrumb({
    category: 'amplitude',
    level: 'info',
    message: eventType,
    data: maskBreadcrumbData(eventProperties),
  });
};

/**
 * Log an event to amplitude.
 * Automatically adds meta properties (ProjectId and GFA) to the event properties.
 * Meta properties are overridden by event properties with the same key.
 * Meta properties are only added if the project is loaded.
 * Also records the same event as a Sentry breadcrumb (no-op when Sentry is not initialized).
 *
 * @param eventType - string identifier for the event, e.g. 'Organization Select', 'Session Start'
 * @param eventProperties - metadata for the event, e.g. { organization: 'Nodon AB' }.
 */
const amplitudeLog = (
  // string identifier for the event, e.g. 'Organization Select', 'Session Start'
  eventType: string,
  // metadata for the event, e.g. { organization: 'Nodon AB' }
  eventProperties: Record<string, unknown> = {},
): void => {
  const metaProperties = getMetaProperties();
  const finalProperties = { ...metaProperties, ...eventProperties };

  if (isEnvironment('development')) {
    console.info(
      '•',
      eventType,
      eventProperties ? JSON.stringify(finalProperties) : '',
    );
  }

  addAmplitudeBreadcrumb(eventType, finalProperties);
  amplitude.track(eventType, finalProperties);
};

export default amplitudeLog;
